import { useCallback, useMemo, useState } from 'react';
import { X, Lock, Users, ChevronDown, Link, UserPlus, Mail } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useWizard } from 'react-use-wizard';

import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';
import { cn } from '@client/lib';
import { selectClient, selectSession,selectFeatureFlags } from '@client/model/selectors';
import {
  AddPartnerDialog,
  InvitePartnerDialog,
  LinkExistingPartnerDialog,
} from '@/features/add-partner';
import { SessionType } from '@shared';

import type { FlatStepConfig } from '@/features/guided-financial-planning/config';
import {
  useSession,
  useIsSessionComplete,
  useGuidedPlanningFlatStepConfigs,
} from '@/features/guided-financial-planning/context';

interface GuidedPlanningHeaderProps {
  title?: string;
}

export function GuidedPlanningHeader({ title }: GuidedPlanningHeaderProps) {
  const { activeStep, goToStep } = useWizard();
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const navigate = useNavigate();
  const { session } = useSession();
  const flatStepConfigs = useGuidedPlanningFlatStepConfigs();
  const isSessionComplete = useIsSessionComplete();
  const hasPartner = !!client?.partnerClientId;
  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const [openAddPartner, setOpenAddPartner] = useState(false);
  const [openInvitePartner, setOpenInvitePartner] = useState(false);
  const [openLinkExisting, setOpenLinkExisting] = useState(false);

  const stepGroups = useMemo((): string[] => {
    return [...new Set(flatStepConfigs.map(s => s.group))];
  }, [flatStepConfigs]);

  const currentStepConfig = flatStepConfigs[activeStep];
  const currentGroup = currentStepConfig?.group;
  const currentGroupTitle = currentStepConfig?.groupTitle;

  const completedSteps = session?.completedSteps ?? [];

  const groupProgress = useMemo(() => {
    return stepGroups.map(group => {
      const groupSteps = flatStepConfigs.filter(s => s.group === group);
      const firstStepIndex = flatStepConfigs.findIndex(s => s.group === group);
      const firstStep = groupSteps[0] as FlatStepConfig | undefined;

      const isCurrentGroup = currentGroup === group;
      // A group is complete if all its trackable steps are in completedSteps
      // (excluding quotes-loading which is a transient loading step, and overview which is a static page)
      const trackableSteps = groupSteps.filter(step => step.id !== 'quotes-loading' && step.id !== 'overview');
      const isPastGroup = activeStep > firstStepIndex + groupSteps.length - 1;
      const isGroupComplete =
        isSessionComplete ||
        (trackableSteps.length > 0
          ? trackableSteps.every(step => completedSteps.includes(step.id as GuidedPlanningStep))
          : isPastGroup);

      return {
        group,
        groupTitle: firstStep?.groupTitle ?? group,
        firstStepIndex,
        isCurrentGroup,
        isGroupComplete,
      };
    });
  }, [currentGroup, completedSteps, flatStepConfigs, activeStep, stepGroups]);

  const handleClose = () => {
    if (client.clientId) {
      const destination = isD2C
        ? `/clients/${client.clientId}/onboarding/care-phases-summary`
        : `/clients/${client.clientId}/onboarding/funding-sources`;
      navigate(destination);
    }
  };

  const handleGroupClick = useCallback(
    (firstStepIndex: number) => {
      goToStep(firstStepIndex);
    },
    [goToStep],
  );

  return (
    <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
      {isSessionComplete && (
        <div className="bg-primary border-b">
          <div className="mx-auto flex max-w-5xl items-center justify-center gap-2 px-4 py-1.5 sm:px-6 sm:py-2">
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <div className="flex cursor-help items-center gap-1.5 sm:gap-2">
                  <Lock className="size-3.5 text-white sm:size-4" />
                  <span className="text-xs font-medium text-white sm:text-sm">
                    This planning session is finalized and view-only
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="max-w-xs space-y-2 p-3"
              >
                <p className="font-medium">Why can't I edit this plan?</p>
                <p className="text-muted-foreground">
                  Finalized plans are locked to preserve an accurate record. All metrics reflect a
                  snapshot at the time of finalization.
                </p>
                <p className="text-muted-foreground text-xs">Editing support coming soon.</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}
      <div
        className={cn(
          'absolute right-2 z-10 flex items-center gap-2 sm:right-4',
          isSessionComplete ? 'top-10 sm:top-11' : 'top-2 sm:top-4',
        )}
      >
        {!hasPartner && !isD2C && isAdvisor && (
          <DropdownMenu>
            <DropdownMenuTrigger className="group">
              <Button
                variant="outline"
                className="rounded-full"
              >
                <Users className="size-4" />
                <span className="hidden sm:inline">Add Partner</span>
                <ChevronDown className="shrink-0 transition-transform group-data-[state=open]:rotate-180" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setOpenLinkExisting(true)}>
                <Link className="size-4" />
                Link Existing Client as Partner
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setOpenAddPartner(true)}>
                <UserPlus className="size-4" />
                Add new partner
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!hasPartner && !isD2C && !isAdvisor && (
          <DropdownMenu>
            <DropdownMenuTrigger className="group">
              <Button
                variant="outline"
                className="rounded-full"
              >
                <Users className="size-4" />
                <span className="hidden sm:inline">Add Partner</span>
                <ChevronDown className="shrink-0 transition-transform group-data-[state=open]:rotate-180" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setOpenAddPartner(true)}>
                <UserPlus className="size-4" />
                Add Partner
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setOpenInvitePartner(true)}>
                <Mail className="size-4" />
                Invite Partner
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button
          variant="outline"
          size="icon"
          onClick={handleClose}
          className="rounded-full"
          aria-label="Close and return to financial planning"
        >
          <X className="size-4 text-gray-700 sm:size-5" />
        </Button>
      </div>
      <div className="mx-auto max-w-5xl space-y-3 p-4 pr-12 sm:space-y-4 sm:p-6 sm:pr-6">
        <div className="flex items-center gap-2 text-sm">
          <Badge className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100">
            Beta
          </Badge>
          <span className="truncate font-semibold text-gray-950">{title ?? currentGroupTitle}</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          {groupProgress.map(
            ({ group, groupTitle, firstStepIndex, isCurrentGroup, isGroupComplete }) => {
              const isClickable = isSessionComplete || isGroupComplete;
              const segmentClasses = cn('h-1 w-full rounded-full transition-colors duration-300', {
                'bg-primary/50': isCurrentGroup,
                'bg-primary': isGroupComplete && !isCurrentGroup,
                'bg-gray-100': !isGroupComplete && !isCurrentGroup,
              });

              return isClickable ? (
                <Tooltip
                  key={group}
                  delayDuration={100}
                >
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => handleGroupClick(firstStepIndex)}
                      className={cn(segmentClasses, 'cursor-pointer hover:opacity-80')}
                      aria-label={`Go to ${groupTitle}`}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{groupTitle}</TooltipContent>
                </Tooltip>
              ) : (
                <div
                  key={group}
                  className={segmentClasses}
                />
              );
            },
          )}
        </div>
      </div>

      <AddPartnerDialog
        open={openAddPartner}
        onOpenChange={setOpenAddPartner}
        clientId={client.clientId}
      />

      <InvitePartnerDialog
        open={openInvitePartner}
        onOpenChange={setOpenInvitePartner}
      />

      {isAdvisor && (
        <LinkExistingPartnerDialog
          open={openLinkExisting}
          client={client}
          onClose={() => setOpenLinkExisting(false)}
        />
      )}
    </div>
  );
}
